import { useEffect, useRef, useState } from "react";
import { Link, Outlet } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ArrowUpRight, CheckCircle2, MailCheck, RefreshCw } from "lucide-react";
import { Button, buttonClass } from "@/components/ui/button";
import { api } from "@/lib/api";
import { t } from "@/i18n/id";
import { useAuth } from "./AuthProvider";

/** Webmail inboxes worth a direct "open" button; everything else just says "check your inbox". */
const INBOXES: Array<[RegExp, string, string]> = [
  [/@(gmail|googlemail)\.com$/i, "Gmail", "https://mail.google.com/mail/u/0/#search/BazeBuild"],
  [/@(yahoo|ymail)\.[a-z.]+$/i, "Yahoo Mail", "https://mail.yahoo.com/"],
  [/@(outlook|hotmail|live|msn)\.[a-z.]+$/i, "Outlook", "https://outlook.live.com/mail/"],
  [/@icloud\.com$/i, "iCloud Mail", "https://www.icloud.com/mail"],
];

const COOLDOWN = 60;

/**
 * Creating and changing websites needs a verified email (the API refuses otherwise). This screen says
 * so plainly, sends the link again (with a cooldown), opens the user's webmail when we know it, and
 * checks by itself, so the user is let through the moment they click the link in another tab.
 */
export function VerifyEmailGate({ onVerified, compact = false }: { onVerified?: () => void; compact?: boolean }) {
  const { user, reloadUser } = useAuth();
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);
  const [stillUnverified, setStillUnverified] = useState(false);
  const resend = useMutation({ mutationFn: () => api.auth.resendVerification(), onSuccess: () => setCooldown(COOLDOWN) });
  const done = useRef(false);

  // Let the user through as soon as they verify (another tab, their phone): check on focus and every 8s.
  useEffect(() => {
    const check = () => void reloadUser().catch(() => undefined);
    const timer = window.setInterval(check, 8000);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, [reloadUser]);
  useEffect(() => {
    if (user?.emailVerified && !done.current) {
      done.current = true;
      onVerified?.();
    }
  }, [user?.emailVerified, onVerified]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  if (!user) return null;
  const inbox = INBOXES.find(([re]) => re.test(user.email));
  const g = t.auth.gate;

  const checkNow = async () => {
    setChecking(true);
    setStillUnverified(false);
    try {
      await reloadUser();
    } finally {
      setChecking(false);
      setStillUnverified(true);
    }
  };

  return (
    <div className={compact ? "py-6" : "grid min-h-dvh place-items-center bg-[#eef1f4] px-4 py-10"}>
      <section aria-labelledby="verify-gate-title" className="w-full max-w-md overflow-hidden rounded-[28px] bg-white text-center shadow-[0_24px_60px_rgb(15_23_42/0.12)] ring-1 ring-slate-900/5">
        <div className="relative bg-[radial-gradient(120%_90%_at_50%_0%,var(--color-primary-soft),transparent_70%)] px-6 pt-10 pb-6">
          <span className="relative mx-auto grid size-20 place-items-center rounded-3xl bg-white shadow-[0_10px_30px_rgb(92_103_212/0.25)] ring-1 ring-primary/15">
            <MailCheck className="size-9 text-primary" aria-hidden="true" />
            <span className="absolute -top-1 -right-1 size-4 animate-ping rounded-full bg-primary/40 motion-reduce:animate-none" aria-hidden="true" />
            <span className="absolute -top-1 -right-1 size-4 rounded-full bg-primary ring-2 ring-white" aria-hidden="true" />
          </span>
          <h1 id="verify-gate-title" className="mt-6 text-xl font-bold tracking-tight text-slate-900">
            {g.title}
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
            {g.body}
            <span className="mt-1 block font-semibold break-all text-slate-900">{user.email}</span>
          </p>
        </div>

        <ol className="mx-6 space-y-2.5 rounded-2xl bg-slate-50 p-4 text-left text-[13px] text-slate-600 ring-1 ring-slate-900/5">
          {g.steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[11px] font-bold text-primary ring-1 ring-primary/20">{i + 1}</span>
              <span className="leading-snug">{s}</span>
            </li>
          ))}
        </ol>

        <div className="space-y-2.5 px-6 pt-5 pb-6">
          {inbox ? (
            <a href={inbox[2]} target="_blank" rel="noopener noreferrer" className={buttonClass("primary", "md", "w-full")}>
              {g.open(inbox[1])}
              <ArrowUpRight aria-hidden="true" />
            </a>
          ) : null}
          <Button variant={inbox ? "secondary" : "primary"} className="w-full" onClick={checkNow} disabled={checking}>
            {checking ? <RefreshCw className="animate-spin" aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}
            {g.check}
          </Button>
          <p aria-live="polite" className="min-h-5 text-[13px] text-slate-500">
            {stillUnverified && !user.emailVerified ? g.notYet : resend.isSuccess && cooldown > 0 ? g.sent : ""}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[13px]">
            <button type="button" onClick={() => resend.mutate()} disabled={resend.isPending || cooldown > 0} className="font-semibold text-primary underline-offset-2 hover:underline disabled:text-slate-400 disabled:no-underline">
              {cooldown > 0 ? g.resendIn(cooldown) : t.auth.verifyResend}
            </button>
            <Link to="/akun" className="text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline">
              {g.wrongEmail}
            </Link>
            {compact ? null : (
              <Link to="/situs" className="text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline">
                {g.back}
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/** Routes that change a website (editor, blog post editor): the gate until the email is verified. */
export function RequireVerifiedEmail() {
  const { user } = useAuth();
  if (user && !user.emailVerified) return <VerifyEmailGate />;
  return <Outlet />;
}
